import moment from 'moment';
import { saveAs } from 'file-saver';
import { appendUnderwritingCalculatorSheet } from '../../../QuickCashflowModel/calc/cloneExcelWorksheet';
import { UW_SCORING_ROW_SPECS } from '../../../QuickCashflowModel/calc/underwritingScorecardScoring';
import {
  buildMonitoringMetricParamsText,
  buildMonitoringScorecardViewModel,
  scoreToRating,
} from './monitoringCompanyScore';
import {
  MONITORING_DECIMAL_NUM_FMT,
  MONITORING_INTEGER_NUM_FMT,
  finiteExportNumber,
  monitoringRoasPctForExcel,
  setExcelMoneyCell,
  setExcelNumericCell,
  setExcelPercentFractionCell,
  setExcelPercentPointsCell,
  writeMonitoringCovenantValueCell,
} from './monitoringExcelNumericCells';
import {
  buildMonitoringRoasQuickViewRow,
  displayMonitoringCovenantKpi,
  filterMonitoringCovenantRows,
  formatMonitoringConstraintValue,
  formatMonitoringCovenantsHeading,
  formatMonitoringRoasCovenantBasis,
  monitoringCovenantStripSummary,
  monitoringKpiPctChange,
  monitoringPctOfCap,
  monitoringRoasRowView,
} from './monitoringHelpers';
import {
  MONITORING_RISK_INPUT_DOLLAR_FIELDS,
  MONITORING_RISK_INPUT_SCORE_FIELDS,
} from './monitoringRiskInputFields';

/** First row below trimmed UW calculator footer (row 31). */
export const MONITORING_UW_COMPANY_PARAMETERS_START_ROW = 33;

export const MONITORING_SHEET_UNDERWRITING = 'Underwriting Calculator';
export const MONITORING_SHEET_COVENANTS_ROAS = 'Covenants & ROAS';
export const MONITORING_SHEET_TOP_KPIS = 'Top KPIs';

export const MONITORING_EXPORT_SHEET_NAMES = [
  MONITORING_SHEET_UNDERWRITING,
  MONITORING_SHEET_COVENANTS_ROAS,
  MONITORING_SHEET_TOP_KPIS,
];

export function buildMonitoringDealPath(customerId, dealId) {
  if (!customerId || !dealId) {
    return null;
  }
  return `/operations/${customerId}/deal/${dealId}`;
}

function sanitizeExportFilenamePart(value) {
  return String(value ?? '')
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function buildMonitoringExportFilename({
  companyName,
  dealName,
  dealId,
  asOfDate,
  reportName,
} = {}) {
  const companyPart = sanitizeExportFilenamePart(companyName);
  const dealPart =
    sanitizeExportFilenamePart(dealName) ||
    sanitizeExportFilenamePart(dealId) ||
    'monitoring';
  let datePart = '';
  if (asOfDate) {
    const parsed = moment(asOfDate, [moment.ISO_8601, 'YYYY-MM-DD'], true);
    datePart = parsed.isValid() ? parsed.format('YYYY-MM-DD') : sanitizeExportFilenamePart(asOfDate);
  }
  const namePart = sanitizeExportFilenamePart(reportName);
  const parts = ['monitoring'];
  if (companyPart) {
    parts.push(companyPart);
  }
  parts.push(dealPart);
  if (datePart) {
    parts.push(datePart);
  }
  if (namePart) {
    parts.push(namePart);
  }
  return `${parts.join('-')}.xlsx`;
}

export function buildMonitoringScoringRowsForExcel(cashflowScorecard, riskInputs) {
  const byKey = new Map();
  (cashflowScorecard || []).forEach((row) => {
    if (row?.key) {
      byKey.set(row.key, row);
    }
  });
  const params = riskInputs || {};
  return UW_SCORING_ROW_SPECS.map((spec) => {
    const row = byKey.get(spec.key);
    const score = row?.score ?? null;
    return {
      ...spec,
      valueText: row?.valueText || null,
      rating: scoreToRating(score),
      score,
      paramsText: buildMonitoringMetricParamsText(spec.key, params, row),
    };
  });
}

function writeSummaryMetricCell(cell, entry) {
  if (entry == null || entry.type === 'text') {
    cell.value = entry?.value ?? '—';
    return;
  }
  if (entry.type === 'money') {
    setExcelMoneyCell(cell, entry.value);
    if (cell.value == null) {
      cell.value = '—';
    }
    return;
  }
  if (entry.type === 'percentFraction') {
    setExcelPercentFractionCell(cell, entry.value);
    if (cell.value == null) {
      cell.value = '—';
    }
    return;
  }
  if (entry.type === 'percentPoints') {
    setExcelPercentPointsCell(cell, entry.value);
    if (cell.value == null) {
      cell.value = '—';
    }
    return;
  }
  if (entry.type === 'number') {
    setExcelNumericCell(cell, entry.value, entry.numFmt ?? MONITORING_DECIMAL_NUM_FMT);
    if (cell.value == null) {
      cell.value = '—';
    }
  }
}

function formatMonitoringCheckLabel(check) {
  if (check === 'pass') {
    return 'Pass';
  }
  if (check === 'fail') {
    return 'Fail';
  }
  return 'N/A';
}

function formatQuickStatusLabel(status) {
  if (status === 'pass') {
    return 'Pass';
  }
  if (status === 'fail') {
    return 'Fail';
  }
  return 'N/A';
}

function writeHeaderRow(worksheet, rowIndex, headers) {
  const row = worksheet.getRow(rowIndex);
  headers.forEach((header, columnIndex) => {
    const cell = row.getCell(columnIndex + 1);
    cell.value = header;
    cell.font = { bold: true };
  });
  return rowIndex + 1;
}

export function writeMonitoringCompanyParametersSection(worksheet, riskInputs) {
  if (!worksheet) {
    return;
  }
  const params = riskInputs || {};
  let rowIndex = MONITORING_UW_COMPANY_PARAMETERS_START_ROW;

  worksheet.getCell(rowIndex, 1).value = 'Company Parameters';
  worksheet.getCell(rowIndex, 1).font = { bold: true, size: 12 };
  rowIndex += 1;

  rowIndex = writeHeaderRow(worksheet, rowIndex, [
    'Parameter',
    'Value',
    'Source',
    'Comment',
  ]);

  for (const field of MONITORING_RISK_INPUT_DOLLAR_FIELDS) {
    const dataRow = worksheet.getRow(rowIndex);
    dataRow.getCell(1).value = field.label;
    setExcelMoneyCell(dataRow.getCell(2), params[field.key], { decimals: 2 });
    if (dataRow.getCell(2).value == null) {
      dataRow.getCell(2).value = '—';
    }
    dataRow.getCell(3).value = params[field.sourceKey] || '—';
    dataRow.getCell(4).value = params[field.commentKey] || '';
    rowIndex += 1;
  }

  for (const field of MONITORING_RISK_INPUT_SCORE_FIELDS) {
    const dataRow = worksheet.getRow(rowIndex);
    dataRow.getCell(1).value = field.label;
    setExcelNumericCell(dataRow.getCell(2), params[field.key], MONITORING_INTEGER_NUM_FMT);
    if (dataRow.getCell(2).value == null) {
      dataRow.getCell(2).value = '—';
    }
    dataRow.getCell(3).value = 'Score 1–3';
    dataRow.getCell(4).value = params[field.commentKey] || '';
    rowIndex += 1;
  }
}

function writeCovenantsRoasSheet(worksheet, fetchResult) {
  let rowIndex = 1;
  const covenantsHeading = formatMonitoringCovenantsHeading({
    targetCohortMonth: fetchResult?.targetCohortMonth,
    closestRepaymentDate: fetchResult?.closestRepaymentDate,
  });
  worksheet.getCell(rowIndex, 1).value = covenantsHeading;
  worksheet.getCell(rowIndex, 1).font = { bold: true, size: 12 };
  rowIndex += 1;

  rowIndex = writeHeaderRow(worksheet, rowIndex, [
    'Area',
    'KPI',
    'Value',
    'Constraint',
    'Check',
  ]);
  const resultRows = filterMonitoringCovenantRows(fetchResult?.resultRows).map(
    (row) => ({
      ...row,
      kpi: displayMonitoringCovenantKpi(row?.kpi),
    }),
  );
  for (const row of resultRows) {
    const dataRow = worksheet.getRow(rowIndex);
    dataRow.getCell(1).value = row.area ?? '';
    dataRow.getCell(2).value = row.kpi ?? '';
    writeMonitoringCovenantValueCell(dataRow.getCell(3), row.kpi, row.value);
    if (dataRow.getCell(3).value == null) {
      dataRow.getCell(3).value = '—';
    }
    dataRow.getCell(4).value = formatMonitoringConstraintValue(row.constraint);
    dataRow.getCell(5).value = formatMonitoringCheckLabel(row.check);
    rowIndex += 1;
  }

  rowIndex += 1;
  const asOfDate = fetchResult?.asOfDate;
  const quickPrevMonthLabel = asOfDate
    ? moment(asOfDate).subtract(1, 'month').format('MMM YYYY')
    : 'Previous';
  const quickCurrMonthLabel = asOfDate
    ? moment(asOfDate).format('MMM YYYY')
    : 'Current';
  const roasRows = fetchResult?.roasCohort?.rows || [];
  const periodLabels = fetchResult?.roasCohort?.periodLabels || [];

  worksheet.getCell(rowIndex, 1).value = 'ROAS Cohort Performance — Quick view';
  worksheet.getCell(rowIndex, 1).font = { bold: true, size: 12 };
  rowIndex += 1;
  rowIndex = writeHeaderRow(worksheet, rowIndex, [
    'Cohort',
    'ROAS basis',
    'CAC',
    'Stage',
    quickPrevMonthLabel,
    quickCurrMonthLabel,
    'Status',
    'Why',
  ]);
  for (const row of roasRows) {
    const rowView = monitoringRoasRowView(row);
    const quick = buildMonitoringRoasQuickViewRow(row, asOfDate);
    const dataRow = worksheet.getRow(rowIndex);
    dataRow.getCell(1).value = row.cohortLabel || row.cohortMonth || '—';
    dataRow.getCell(2).value = formatMonitoringRoasCovenantBasis(row);
    setExcelMoneyCell(dataRow.getCell(3), row.cac);
    if (dataRow.getCell(3).value == null) {
      dataRow.getCell(3).value = '—';
    }
    dataRow.getCell(4).value = row.status || '—';
    const prevPct = monitoringRoasPctForExcel(
      quick.previousCell,
      rowView,
      row.covenantMultiplier,
    );
    const currPct = monitoringRoasPctForExcel(
      quick.currentCell,
      rowView,
      row.covenantMultiplier,
    );
    setExcelPercentFractionCell(dataRow.getCell(5), prevPct);
    if (dataRow.getCell(5).value == null) {
      dataRow.getCell(5).value = '—';
    }
    setExcelPercentFractionCell(dataRow.getCell(6), currPct);
    if (dataRow.getCell(6).value == null) {
      dataRow.getCell(6).value = '—';
    }
    dataRow.getCell(7).value = formatQuickStatusLabel(quick.status);
    dataRow.getCell(8).value = quick.reason || '—';
    rowIndex += 1;
  }

  rowIndex += 1;
  worksheet.getCell(rowIndex, 1).value = 'ROAS Cohort Performance — Full view';
  worksheet.getCell(rowIndex, 1).font = { bold: true, size: 12 };
  rowIndex += 1;
  rowIndex = writeHeaderRow(worksheet, rowIndex, [
    'Cohort',
    'ROAS basis',
    'CAC',
    'Stage',
    ...periodLabels.map((period) => `P${period}`),
  ]);
  for (const row of roasRows) {
    const rowView = monitoringRoasRowView(row);
    const dataRow = worksheet.getRow(rowIndex);
    dataRow.getCell(1).value = row.cohortLabel || row.cohortMonth || '—';
    dataRow.getCell(2).value = formatMonitoringRoasCovenantBasis(row);
    setExcelMoneyCell(dataRow.getCell(3), row.cac);
    if (dataRow.getCell(3).value == null) {
      dataRow.getCell(3).value = '—';
    }
    dataRow.getCell(4).value = row.status || '—';
    (row.cells || []).forEach((roasCell, cellIndex) => {
      const pct = monitoringRoasPctForExcel(
        roasCell,
        rowView,
        row.covenantMultiplier,
      );
      const target = dataRow.getCell(5 + cellIndex);
      setExcelPercentFractionCell(target, pct);
      if (target.value == null) {
        target.value = '—';
      }
    });
    rowIndex += 1;
  }

  worksheet.getColumn(1).width = 22;
  worksheet.getColumn(2).width = 14;
  worksheet.getColumn(3).width = 14;
  worksheet.getColumn(7).width = 40;
}

function writeKpiPairRows(worksheet, startRow, sectionTitle, pairs) {
  let rowIndex = startRow;
  worksheet.getCell(rowIndex, 1).value = sectionTitle;
  worksheet.getCell(rowIndex, 1).font = { bold: true, size: 12 };
  rowIndex += 1;
  rowIndex = writeHeaderRow(worksheet, rowIndex, [
    'Metric',
    'Current',
    'Prior',
    '% Change',
  ]);
  for (const pair of pairs) {
    const pct = monitoringKpiPctChange(pair.current, pair.prior);
    const dataRow = worksheet.getRow(rowIndex);
    dataRow.getCell(1).value = pair.label;
    if (pair.valueType === 'money') {
      setExcelMoneyCell(dataRow.getCell(2), pair.current);
      setExcelMoneyCell(dataRow.getCell(3), pair.prior);
    } else {
      setExcelNumericCell(
        dataRow.getCell(2),
        pair.current,
        MONITORING_INTEGER_NUM_FMT,
      );
      setExcelNumericCell(
        dataRow.getCell(3),
        pair.prior,
        MONITORING_INTEGER_NUM_FMT,
      );
    }
    if (dataRow.getCell(2).value == null) {
      dataRow.getCell(2).value = '—';
    }
    if (dataRow.getCell(3).value == null) {
      dataRow.getCell(3).value = '—';
    }
    setExcelPercentPointsCell(dataRow.getCell(4), pct);
    if (dataRow.getCell(4).value == null) {
      dataRow.getCell(4).value = '—';
    }
    rowIndex += 1;
  }
  return rowIndex + 1;
}

function writeTopKpisSheet(
  worksheet,
  fetchResult,
  riskInputs,
  companyName,
) {
  const scoreView = buildMonitoringScorecardViewModel(
    fetchResult?.cashflowScorecard || [],
    riskInputs || fetchResult?.riskInputs || {},
  );
  const rated = (scoreView.valueRows || []).filter((r) => r.score !== '—');
  const scorePctFraction = finiteExportNumber(fetchResult?.riskScoreFinalPct);
  const weighted =
    rated.length > 0
      ? rated.reduce((sum, r) => sum + Number(r.score || 0), 0) / rated.length
      : null;

  const covenants = monitoringCovenantStripSummary(
    fetchResult?.resultRows,
    fetchResult?.roasCohort?.rows,
  );
  const exposure = fetchResult?.postFundingExposure;
  const maxExposure = fetchResult?.dealConstraints?.maxExposure;
  const exposurePct = monitoringPctOfCap(exposure, maxExposure);
  const funding = fetchResult?.requestedFundingAmount;
  const maxFunding = fetchResult?.dealConstraints?.maxFundingAmount;
  const fundingPct = monitoringPctOfCap(funding, maxFunding);
  const strip = fetchResult?.kpiStrip || {};
  const failedList =
    covenants.failedNames?.length > 0 ? covenants.failedNames.join(', ') : null;

  let rowIndex = 1;
  worksheet.getCell(rowIndex, 1).value = 'Summary';
  worksheet.getCell(rowIndex, 1).font = { bold: true, size: 12 };
  rowIndex += 1;
  rowIndex = writeHeaderRow(worksheet, rowIndex, ['Metric', 'Value']);
  const summaryRows = [
    { label: 'Customer', value: companyName?.trim() || '—', type: 'text' },
    { label: 'Company Score %', value: scorePctFraction, type: 'percentFraction' },
    {
      label: 'Company Score Label',
      value: fetchResult?.riskScoreFinalLabel || '—',
      type: 'text',
    },
    {
      label: 'Rated metrics',
      value: `Rated ${rated.length} / ${(scoreView.valueRows || []).length} metrics`,
      type: 'text',
    },
    { label: 'Weighted score', value: weighted, type: 'number' },
    {
      label: 'Covenants passed',
      value:
        covenants.total > 0 ? `${covenants.passed} / ${covenants.total}` : '—',
      type: 'text',
    },
    { label: 'Failed covenants', value: failedList || '—', type: 'text' },
    { label: 'Post-Funding Exposure', value: exposure, type: 'money' },
    {
      label: 'Post-Funding Exposure % of limit',
      value: exposurePct,
      type: 'percentPoints',
    },
    { label: 'Requested Funding Amount', value: funding, type: 'money' },
    {
      label: 'Requested Funding % of max',
      value: fundingPct,
      type: 'percentPoints',
    },
    {
      label: 'Target cohort',
      value: fetchResult?.targetCohortMonth || '—',
      type: 'text',
    },
    { label: 'As of date', value: fetchResult?.asOfDate || '—', type: 'text' },
    {
      label: 'Deal',
      value:
        fetchResult?.dealConstraints?.dealName ||
        fetchResult?.dealConstraints?.dealId ||
        '—',
      type: 'text',
    },
  ];
  for (const entry of summaryRows) {
    const dataRow = worksheet.getRow(rowIndex);
    dataRow.getCell(1).value = entry.label;
    writeSummaryMetricCell(dataRow.getCell(2), entry);
    rowIndex += 1;
  }

  rowIndex += 1;
  const mtdCur = strip.mtdCurrentLabel || 'This month';
  const mtdPrior = strip.mtdPriorLabel || 'Last month';
  rowIndex = writeKpiPairRows(
    worksheet,
    rowIndex,
    `Month to Date — ${mtdCur} vs ${mtdPrior}`,
    [
      {
        label: 'Marketing Spend',
        current: strip.marketingSpendMtd?.current,
        prior: strip.marketingSpendMtd?.prior,
        valueType: 'money',
      },
      {
        label: 'Revenue',
        current: strip.revenueMtd?.current,
        prior: strip.revenueMtd?.prior,
        valueType: 'money',
      },
      {
        label: 'New Payers',
        current: strip.newPayersMtd?.current,
        prior: strip.newPayersMtd?.prior,
        valueType: 'integer',
      },
      {
        label: 'Returning Payers',
        current: strip.returningPayersMtd?.current,
        prior: strip.returningPayersMtd?.prior,
        valueType: 'integer',
      },
    ],
  );

  const outCur = strip.outlookCurrentLabel || 'Projected';
  const outPrior = strip.outlookPriorLabel || 'Prior actual';
  writeKpiPairRows(worksheet, rowIndex, `Full-Month Outlook — ${outCur} vs ${outPrior}`, [
    {
      label: 'Marketing Spend — Projected Month',
      current: strip.marketingSpendProjected?.current,
      prior: strip.marketingSpendProjected?.prior,
      valueType: 'money',
    },
    {
      label: 'Revenue — Projected Month',
      current: strip.revenueProjected?.current,
      prior: strip.revenueProjected?.prior,
      valueType: 'money',
    },
  ]);

  worksheet.getColumn(1).width = 36;
  worksheet.getColumn(2).width = 28;
  worksheet.getColumn(3).width = 18;
  worksheet.getColumn(4).width = 14;
}

export async function buildMonitoringWorkbook({
  fetchResult,
  riskInputs,
  scorecardConfig = null,
  companyName = null,
  templateBuffer = null,
  ExcelJS: ExcelJSImport = null,
}) {
  if (!fetchResult) {
    throw new Error('fetchResult is required');
  }
  const ExcelJS = ExcelJSImport ?? (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const params = riskInputs ?? fetchResult.riskInputs ?? {};
  const scoringRows = buildMonitoringScoringRowsForExcel(
    fetchResult.cashflowScorecard,
    params,
  );

  await appendUnderwritingCalculatorSheet(workbook, ExcelJS, {
    templateBuffer,
    scorecardConfig,
    scoringRows,
    monthlyOpex: params?.monthlyOpex,
    companyName: companyName?.trim() || null,
  });

  const underwritingSheet = workbook.getWorksheet(MONITORING_SHEET_UNDERWRITING);
  writeMonitoringCompanyParametersSection(underwritingSheet, params);

  const covenantsSheet = workbook.addWorksheet(MONITORING_SHEET_COVENANTS_ROAS);
  writeCovenantsRoasSheet(covenantsSheet, fetchResult);

  const kpiSheet = workbook.addWorksheet(MONITORING_SHEET_TOP_KPIS);
  writeTopKpisSheet(
    kpiSheet,
    fetchResult,
    params,
    companyName,
  );

  return workbook;
}

export async function downloadMonitoringExcel({
  fetchResult,
  riskInputs,
  reportName,
  scorecardConfig = null,
  companyName = null,
  templateBuffer = null,
  ExcelJS = null,
  saveAsImpl = saveAs,
}) {
  const workbook = await buildMonitoringWorkbook({
    fetchResult,
    riskInputs,
    scorecardConfig,
    companyName,
    templateBuffer,
    ExcelJS,
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const filename = buildMonitoringExportFilename({
    companyName,
    dealName: fetchResult?.dealConstraints?.dealName,
    dealId: fetchResult?.dealConstraints?.dealId,
    asOfDate: fetchResult?.asOfDate,
    reportName,
  });
  saveAsImpl(blob, filename);
  return filename;
}
